import { type ServerProvider } from "@t3tools/contracts";
import { memo, useEffect, useRef } from "react";
import { CircleAlertIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { formatProviderDriverKindLabel } from "../../providerModels";
import { Alert, AlertAction, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

export const PROVIDER_STATUS_BANNER_AUTO_DISMISS_MS = 8_000;

export function getProviderStatusBannerKey(status: ServerProvider | null): string | null {
  return !status || status.status === "ready" || status.status === "disabled"
    ? null
    : [status.instanceId, status.status, status.auth.status, status.message ?? ""].join("\u0000");
}

export function shouldShowProviderStatusBanner(
  status: ServerProvider | null,
  dismissedBannerKey: string | null,
): boolean {
  const bannerKey = getProviderStatusBannerKey(status);
  return bannerKey !== null && bannerKey !== dismissedBannerKey;
}

export function scheduleProviderStatusBannerDismiss(
  bannerKey: string | null,
  onDismiss: () => void,
): () => void {
  if (bannerKey === null) {
    return () => {};
  }
  const timeoutId = setTimeout(onDismiss, PROVIDER_STATUS_BANNER_AUTO_DISMISS_MS);
  return () => clearTimeout(timeoutId);
}

export const ProviderStatusBanner = memo(function ProviderStatusBanner({
  onDismiss,
  status,
}: {
  onDismiss: () => void;
  status: ServerProvider | null;
}) {
  const bannerKey = getProviderStatusBannerKey(status);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  useEffect(
    () => scheduleProviderStatusBannerDismiss(bannerKey, () => onDismissRef.current()),
    [bannerKey],
  );

  if (!status || bannerKey === null) {
    return null;
  }

  const providerName = status.displayName?.trim() || formatProviderDriverKindLabel(status.driver);
  const isUnauthenticated = status.status === "error" && status.auth.status === "unauthenticated";
  const isIncompatible = status.status === "incompatible";
  const title = isUnauthenticated
    ? `Sign in to ${providerName}`
    : isIncompatible
      ? `${providerName} is incompatible`
      : status.status === "error"
        ? `${providerName} is unavailable`
        : `${providerName} compatibility check incomplete`;
  const message = isUnauthenticated
    ? "Sign in via the CLI to authenticate again."
    : (status.message ??
      (isIncompatible
        ? "Install matching provider runtime and helper versions, then refresh provider status."
        : status.status === "error"
          ? `${providerName} provider is unavailable.`
          : `${providerName} provider has limited availability.`));
  const severity = status.status === "warning" ? "warning" : "error";

  return (
    <div
      className="pointer-events-auto ml-auto w-full max-w-sm"
      data-layout="overlay"
      data-provider-status-banner
    >
      <Alert
        variant={severity}
        data-severity={severity}
        className="w-full rounded-lg bg-background/95 px-3 py-2 shadow-lg backdrop-blur-sm"
      >
        {severity === "warning" ? (
          <TriangleAlertIcon aria-hidden />
        ) : (
          <CircleAlertIcon aria-hidden />
        )}
        <AlertTitle className="min-w-0 leading-5">
          <Tooltip>
            <TooltipTrigger render={<span className="block truncate" />}>{title}</TooltipTrigger>
            <TooltipPopup side="bottom" align="end" className="max-w-96 whitespace-pre-wrap">
              {message}
            </TooltipPopup>
          </Tooltip>
        </AlertTitle>
        <AlertAction>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            aria-label={`Dismiss ${providerName} provider ${status.status}`}
            className={
              severity === "warning"
                ? "text-warning hover:bg-warning/10 hover:text-warning"
                : "text-destructive hover:bg-destructive/10 hover:text-destructive"
            }
            onClick={onDismiss}
          >
            <XIcon aria-hidden />
          </Button>
        </AlertAction>
      </Alert>
    </div>
  );
});
